import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import { MessageSquare, Plus, History, Send, ChevronRight, FileText, CheckCircle2, AlertCircle } from 'lucide-react';
import { formatSupportTicketMessage, getOrCreateSupportSessionId, parseSupportTicketMessage, SUPPORT_NOTIFICATION_EMAIL } from '../services/supportSession';

interface Ticket {
    id: string;
    status: string;
    category: string;
    created_at: string;
    updated_at?: string;
    subject?: string;
}

const escapeHtml = (value: string) => value.replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
}[char] || char));

export const SupportPage: React.FC = () => {
    const navigate = useNavigate();
    const [session, setSession] = useState<Session | null>(null);
    const [activeTab, setActiveTab] = useState<'create' | 'view'>('create');
    const [loading, setLoading] = useState(false);
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        plan: 'Free',
        category: 'General Inquiry',
        subject: '',
        body: ''
    });
    const [submittedTicket, setSubmittedTicket] = useState<{ id: string, subject: string } | null>(null);

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
            setSession(currentSession);

            if (currentSession?.user) {
                setFormData(prev => ({
                    ...prev,
                    name: currentSession.user.user_metadata?.full_name || '',
                    email: currentSession.user.email || ''
                }));
                fetchTickets(currentSession.user.id);
            } else {
                const sessionId = getOrCreateSupportSessionId();
                fetchTicketsBySession(sessionId);
            }
        });
    }, []);

    const hydrateTicketSubjects = async (rawTickets: Ticket[]) => {
        if (rawTickets.length === 0) {
            setTickets([]);
            return;
        }

        const { data: messageData, error: messageError } = await supabase
            .from('support_messages')
            .select('ticket_id, content, created_at')
            .in('ticket_id', rawTickets.map((ticket) => ticket.id))
            .order('created_at', { ascending: true });

        if (messageError || !messageData) {
            if (messageError) {
                console.error('Error hydrating support ticket subjects:', messageError);
            }
            setTickets(rawTickets);
            return;
        }

        const subjectByTicketId = new Map<string, string>();

        for (const message of messageData) {
            if (!subjectByTicketId.has(message.ticket_id)) {
                const parsed = parseSupportTicketMessage(message.content);
                if (parsed.subject) {
                    subjectByTicketId.set(message.ticket_id, parsed.subject);
                }
            }
        }

        setTickets(rawTickets.map((ticket) => ({
            ...ticket,
            subject: subjectByTicketId.get(ticket.id) || ticket.subject
        })));
    };

    const fetchTickets = async (userId: string) => {
        const { data, error } = await supabase
            .from('support_tickets')
            .select('*')
            .eq('user_id', userId)
            .order('updated_at', { ascending: false });

        if (error) {
            console.error('Error fetching support tickets:', error);
            return;
        }

        await hydrateTicketSubjects(data || []);
    };

    const fetchTicketsBySession = async (sessionId: string) => {
        const { data, error } = await supabase
            .from('support_tickets')
            .select('*')
            .eq('session_id', sessionId)
            .order('updated_at', { ascending: false });

        if (error) {
            console.error('Error fetching guest support tickets:', error);
            return;
        }

        await hydrateTicketSubjects(data || []);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const sessionId = session?.user ? null : getOrCreateSupportSessionId();
            const name = formData.name.trim();
            const email = formData.email.trim();
            const subject = formData.subject.trim();
            const body = formData.body.trim();
            const initialMessage = formatSupportTicketMessage(subject, body);

            const ticketData = {
                user_id: session?.user?.id || null,
                session_id: sessionId,
                name,
                email,
                plan: formData.plan,
                category: formData.category,
                status: 'open'
            };

            const { data: ticket, error: ticketError } = await supabase
                .from('support_tickets')
                .insert(ticketData)
                .select()
                .single();

            if (ticketError) throw ticketError;

            // Add initial message
            const { error: msgError } = await supabase
                .from('support_messages')
                .insert({
                    ticket_id: ticket.id,
                    sender_type: 'user',
                    content: initialMessage
                });

            if (msgError) throw msgError;

            // Send email notification to admin via Supabase Function
            const { error: emailError } = await supabase.functions.invoke('send-email', {
                body: {
                    to_email: SUPPORT_NOTIFICATION_EMAIL,
                    to_name: 'ForexFund Support',
                    custom_subject: `New Support Ticket: ${subject}`,
                    custom_html: `
            <h2>New Support Ticket Received</h2>
            <p><strong>From:</strong> ${escapeHtml(name)} (${escapeHtml(email)})</p>
            <p><strong>Plan:</strong> ${escapeHtml(formData.plan)}</p>
            <p><strong>Category:</strong> ${escapeHtml(formData.category)}</p>
            <p><strong>Subject:</strong> ${escapeHtml(subject)}</p>
            <p><strong>Ticket ID:</strong> ${ticket.id}</p>
            <hr/>
            <p>${escapeHtml(body).replace(/\n/g, '<br/>')}</p>
          `
                }
            });

            if (emailError) {
                console.error('Error sending support notification email:', emailError);
            }

            setSubmittedTicket({ id: ticket.id, subject: formData.subject.trim() });
            
            if (session?.user?.id) await fetchTickets(session.user.id);
            else if (sessionId) await fetchTicketsBySession(sessionId);

            setFormData(prev => ({
                ...prev,
                subject: '',
                body: ''
            }));
        } catch (error: any) {
            console.error('Error creating ticket:', error);
            alert('Failed to create ticket. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#060810] pt-24 pb-12 relative overflow-hidden">
            <Helmet>
                <title>ForexFund AI Support — Institutional Help Center</title>
                <meta name="description" content="Need assistance with your trading dashboard or autotrade setup? Contact our support team or open a ticket for institutional-grade help." />
                <link rel="canonical" href="https://www.forexfundai.com/support" />
                <script type="application/ld+json">
                    {`
                    {
                        "@context": "https://schema.org",
                        "@type": "ContactPage",
                        "name": "ForexFund AI Support Center",
                        "description": "24/7 institutional support for ForexFund AI users. Open a ticket or contact our strategist team.",
                        "url": "https://www.forexfundai.com/support"
                    }
                    `}
                </script>
            </Helmet>
            {/* Background Effects */}
            <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#D4A537]/5 blur-[120px] rounded-full pointer-events-none"></div>
            <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-500/5 blur-[120px] rounded-full pointer-events-none"></div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 border border-[#D4A537]/20 rounded-full bg-[#D4A537]/5 animate-pulse">
                        <MessageSquare className="w-4 h-4 text-[#D4A537]" />
                        <span className="text-[11px] font-black tracking-[2px] uppercase text-[#D4A537]">Help Center</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight mb-4">
                        Support <span className="text-[#D4A537] drop-shadow-[0_0_15px_rgba(212,165,55,0.3)]">Terminal</span>
                    </h1>
                    <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                        Experience institutional-grade support. Resolve technical issues or inquire about our AI-driven strategies.
                    </p>
                </div>

                {/* Navigation Tabs */}
                <div className="flex justify-center mb-10">
                    <div className="bg-[#131722]/80 backdrop-blur-xl p-1.5 rounded-2xl border border-white/5 flex gap-1 shadow-2xl">
                        <button
                            onClick={() => setActiveTab('create')}
                            className={`flex items-center gap-2 px-8 py-3 rounded-xl font-black text-xs tracking-widest transition-all duration-300 ${activeTab === 'create'
                                ? 'bg-[#D4A537] text-black shadow-lg shadow-[#D4A537]/30 scale-105'
                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            <Plus className="w-4 h-4" />
                            CREATE TICKET
                        </button>
                        <button
                            onClick={() => setActiveTab('view')}
                            className={`flex items-center gap-2 px-8 py-3 rounded-xl font-black text-xs tracking-widest transition-all duration-300 ${activeTab === 'view'
                                ? 'bg-[#D4A537] text-black shadow-lg shadow-[#D4A537]/30 scale-105'
                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            <History className="w-4 h-4" />
                            MY TICKETS
                        </button>
                    </div>
                </div>

                {/* Content Area */}
                <div className="bg-[#131722]/60 backdrop-blur-2xl rounded-3xl border border-white/5 shadow-[0_32px_64px_-12px_rgba(0,0,0,0.5)] overflow-hidden transition-all duration-500">
                    {submittedTicket ? (
                        <div className="p-8 md:p-16 text-center space-y-8 animate-in fade-in zoom-in duration-500">
                            <div className="w-24 h-24 bg-gradient-to-br from-emerald-500 to-emerald-400 rounded-full flex items-center justify-center mx-auto shadow-[0_0_50px_rgba(16,185,129,0.4)]">
                                <CheckCircle2 className="w-12 h-12 text-black" />
                            </div>
                            
                            <div>
                                <h2 className="text-3xl font-black text-white uppercase tracking-tight mb-2">Ticket Secured</h2>
                                <p className="text-gray-400">Our analysts have been notified. Expect a response within 2.4 hours.</p>
                            </div>

                            <div className="max-w-md mx-auto p-6 bg-white/5 border border-white/10 rounded-2xl">
                                <p className="text-[11px] font-black text-gray-500 uppercase tracking-[2px] mb-2">Tracking ID</p>
                                <p className="text-finance-accent font-black text-xl tracking-widest uppercase">{submittedTicket.id}</p>
                                <p className="text-[11px] text-gray-600 mt-4 italic">"{submittedTicket.subject}"</p>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    onClick={() => navigate(`/support/ticket/${submittedTicket.id}`)}
                                    className="px-8 py-4 bg-white/5 border border-white/10 text-white font-black text-xs tracking-widest uppercase rounded-xl hover:bg-white/10 transition-all flex items-center justify-center gap-2"
                                >
                                    <FileText className="w-4 h-4" />
                                    VIEW TICKET
                                </button>
                                <button
                                    onClick={() => {
                                        setSubmittedTicket(null);
                                        setActiveTab('view');
                                    }}
                                    className="px-8 py-4 bg-[#D4A537] text-black font-black text-xs tracking-widest uppercase rounded-xl hover:bg-yellow-400 transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2"
                                >
                                    <History className="w-4 h-4" />
                                    MY TICKETS
                                </button>
                                <button
                                    onClick={() => setSubmittedTicket(null)}
                                    className="px-8 py-4 text-gray-500 font-bold text-xs tracking-widest uppercase hover:text-white transition-all"
                                >
                                    NEW TICKET
                                </button>
                            </div>
                        </div>
                    ) : activeTab === 'create' ? (
                        <form onSubmit={handleSubmit} className="p-8 md:p-12 space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <label className="block text-[11px] font-black text-gray-500 uppercase tracking-[2px] mb-3 ml-1">Customer Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.name}
                                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full bg-[#060810]/50 border border-white/10 rounded-2xl px-5 py-4 text-white focus:border-[#D4A537]/50 focus:ring-1 focus:ring-[#D4A537]/50 outline-none transition-all placeholder:text-gray-700"
                                        placeholder="Full Name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-black text-gray-500 uppercase tracking-[2px] mb-3 ml-1">Email Address</label>
                                    <input
                                        type="email"
                                        required
                                        value={formData.email}
                                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                                        className="w-full bg-[#060810]/50 border border-white/10 rounded-2xl px-5 py-4 text-white focus:border-[#D4A537]/50 focus:ring-1 focus:ring-[#D4A537]/50 outline-none transition-all placeholder:text-gray-700"
                                        placeholder="Email Address"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <label className="block text-[11px] font-black text-gray-500 uppercase tracking-[2px] mb-3 ml-1">Account Plan</label>
                                    <div className="relative">
                                        <select
                                            value={formData.plan}
                                            onChange={e => setFormData({ ...formData, plan: e.target.value })}
                                            className="w-full bg-[#060810]/50 border border-white/10 rounded-2xl px-5 py-4 text-white focus:border-[#D4A537]/50 focus:ring-1 focus:ring-[#D4A537]/50 outline-none transition-all appearance-none cursor-pointer"
                                        >
                                            <option value="Free">Free Account</option>
                                            <option value="Pro">Pro Institutional</option>
                                            <option value="Elite">Elite Quant</option>
                                        </select>
                                        <ChevronRight className="absolute right-5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 rotate-90 pointer-events-none" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[11px] font-black text-gray-500 uppercase tracking-[2px] mb-3 ml-1">Ticket Category</label>
                                    <div className="relative">
                                        <select
                                            value={formData.category}
                                            onChange={e => setFormData({ ...formData, category: e.target.value })}
                                            className="w-full bg-[#060810]/50 border border-white/10 rounded-2xl px-5 py-4 text-white focus:border-[#D4A537]/50 focus:ring-1 focus:ring-[#D4A537]/50 outline-none transition-all appearance-none cursor-pointer"
                                        >
                                            <option value="General Inquiry">General Inquiry</option>
                                            <option value="Technical Support">Technical Support</option>
                                            <option value="Billing">Billing & Subscription</option>
                                            <option value="Strategy Help">Strategy Performance</option>
                                        </select>
                                        <ChevronRight className="absolute right-5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 rotate-90 pointer-events-none" />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-[11px] font-black text-gray-500 uppercase tracking-[2px] mb-3 ml-1">Subject</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.subject}
                                    onChange={e => setFormData({ ...formData, subject: e.target.value })}
                                    className="w-full bg-[#060810]/50 border border-white/10 rounded-2xl px-5 py-4 text-white focus:border-[#D4A537]/50 focus:ring-1 focus:ring-[#D4A537]/50 outline-none transition-all placeholder:text-gray-700"
                                    placeholder="Brief summary of the issue"
                                />
                            </div>

                            <div>
                                <label className="block text-[11px] font-black text-gray-500 uppercase tracking-[2px] mb-3 ml-1">Detailed Message</label>
                                <textarea
                                    required
                                    rows={6}
                                    value={formData.body}
                                    onChange={e => setFormData({ ...formData, body: e.target.value })}
                                    className="w-full bg-[#060810]/50 border border-white/10 rounded-2xl px-5 py-4 text-white focus:border-[#D4A537]/50 focus:ring-1 focus:ring-[#D4A537]/50 outline-none transition-all resize-none placeholder:text-gray-700"
                                    placeholder="Please describe your issue in detail..."
                                ></textarea>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-5 bg-gradient-to-r from-[#F0C060] via-[#D4A537] to-[#A07820] text-black font-black uppercase tracking-[3px] text-xs rounded-2xl transition-all duration-300 shadow-[0_20px_40px_-10px_rgba(212,165,55,0.4)] hover:shadow-[0_25px_50px_-12px_rgba(212,165,55,0.6)] hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed group"
                            >
                                {loading ? 'PROCESSING REQUEST...' : (
                                    <>
                                        SUBMIT SECURE TICKET
                                        <Send className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                                    </>
                                )}
                            </button>
                        </form>
                    ) : (
                        <div className="p-8 md:p-12">
                            {tickets.length > 0 ? (
                                <div className="space-y-6">
                                    {tickets.map(ticket => (
                                        <button
                                            key={ticket.id}
                                            type="button"
                                            onClick={() => navigate(`/support/ticket/${ticket.id}`)}
                                            className="w-full group bg-[#060810]/30 border border-white/5 hover:border-[#D4A537]/30 rounded-2xl p-6 transition-all duration-300 cursor-pointer flex items-center justify-between text-left"
                                        >
                                            <div className="flex items-center gap-6">
                                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${ticket.status === 'open' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-gray-500/10 text-gray-400'
                                                    }`}>
                                                    <FileText className="w-6 h-6" />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <span className="text-white font-bold text-lg group-hover:text-[#D4A537] transition-colors">{ticket.subject || `${ticket.category} Ticket`}</span>
                                                        <span className={`text-[11px] font-black uppercase tracking-[2px] px-3 py-1 rounded-full ${ticket.status === 'open' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20' : 'bg-gray-800 text-gray-400 border border-gray-700'
                                                            }`}>
                                                            {ticket.status}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-4 text-xs text-gray-500">
                                                        <span className="flex items-center gap-1.5 uppercase tracking-wider font-bold">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-[#D4A537]"></div>
                                                            {ticket.category}
                                                        </span>
                                                        <span className="text-gray-800 select-none">|</span>
                                                        <span className="flex items-center gap-1.5 grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all">
                                                            <AlertCircle className="w-3.5 h-3.5" />
                                                            Activity {new Date(ticket.updated_at || ticket.created_at).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="w-10 h-10 rounded-full border border-white/5 flex items-center justify-center text-gray-600 group-hover:border-[#D4A537]/30 group-hover:text-[#D4A537] transition-all">
                                                <ChevronRight className="w-5 h-5" />
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-20">
                                    <div className="w-20 h-20 bg-white/5 rounded-3xl flex items-center justify-center mx-auto mb-8 rotate-12 group-hover:rotate-0 transition-transform duration-500">
                                        <History className="w-10 h-10 text-gray-600" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-white mb-3">No Active Tickets</h3>
                                    <p className="text-gray-500 mb-10 max-w-sm mx-auto">You haven't submitted any support tickets yet. Our team is ready to assist you.</p>
                                    <button
                                        onClick={() => setActiveTab('create')}
                                        className="px-8 py-4 bg-[#D4A537]/10 border border-[#D4A537]/20 text-[#D4A537] font-black text-xs tracking-widest uppercase rounded-xl hover:bg-[#D4A537]/20 transition-all shadow-2xl"
                                    >
                                        SUBMIT YOUR FIRST TICKET
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer Info Cards */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="group bg-[#131722]/40 backdrop-blur-xl p-8 rounded-3xl border border-white/5 text-center hover:border-[#10B981]/30 transition-all duration-500">
                        <div className="w-16 h-16 bg-[#10B981]/10 rounded-2xl flex items-center justify-center mx-auto mb-6 text-[#10B981]">
                            <CheckCircle2 className="w-8 h-8" />
                        </div>
                        <h4 className="text-white font-bold mb-3 uppercase tracking-wider text-sm">Response Time</h4>
                        <p className="text-[#10B981] font-black text-3xl tracking-tighter mb-2">2.4 HOURS</p>
                        <p className="text-gray-500 text-xs">Averaged over last 30 days</p>
                    </div>
                    <div className="group bg-[#131722]/40 backdrop-blur-xl p-8 rounded-3xl border border-white/5 text-center hover:border-[#D4A537]/30 transition-all duration-500">
                        <div className="w-16 h-16 bg-[#D4A537]/10 rounded-2xl flex items-center justify-center mx-auto mb-6 text-[#D4A537]">
                            <MessageSquare className="w-8 h-8" />
                        </div>
                        <h4 className="text-white font-bold mb-3 uppercase tracking-wider text-sm">Live Analyst</h4>
                        <p className="text-gray-300 font-bold text-lg mb-2">Real-time Assistance</p>
                        <p className="text-gray-500 text-xs">Available 24/5 for institutional clients</p>
                    </div>
                    <div className="group bg-[#131722]/40 backdrop-blur-xl p-8 rounded-3xl border border-white/5 text-center hover:border-blue-500/30 transition-all duration-500">
                        <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6 text-blue-400">
                            <Send className="w-8 h-8" />
                        </div>
                        <h4 className="text-white font-bold mb-3 uppercase tracking-wider text-sm">Official Email</h4>
                        <p className="text-gray-300 font-bold text-sm sm:text-base md:text-lg mb-2 break-all">{SUPPORT_NOTIFICATION_EMAIL}</p>
                        <p className="text-gray-500 text-xs">Direct encrypted communication</p>
                    </div>
                </div>
            </div>
        </div>
    );
};
